/**
 * «Договори» (/contracts) — РЕДИЗАЙН (VITE_REDESIGN).
 *
 * Прод без прапорця бачить незмінений ContractsPageLegacy у page.tsx: маршрут
 * не гейтований жодним VITE_FEATURE_*, тож у прод-збірці сторінка присутня.
 *
 * Що виправлено проти старої версії:
 *  · власна палітра (#1A1A1A / #E0E0E0 / #6B6B70 / #F5F5F7 і статуси
 *    #0D7A3C / #B8860B / var(--accent)) — на токени;
 *  · статус expired був червоним — протермінований договір не є збоєм системи,
 *    за §3 це --warn; --err на цій сторінці не потрібен узагалі;
 *  · статуси виводились сирим enum'ом англійською (active / draft / pending …)
 *    в україномовному інтерфейсі — тепер через i18n contract_status_*;
 *  · явний Inter на h1 — §0 забороняє в новому коді;
 *  · заголовок у тілі прибрано, у топбарі — «Договори» (як у меню; ключ
 *    contracts_title каже «Контракти», через що сайдбар і шапка розходились);
 *  · пошук переїхав у топбар (§6, useContractsUi);
 *  · рядки таблиці не мали hover, хоча змінна для нього була оголошена.
 *
 * Запит, обробка 404, тости й клієнтський пошук — той самий код.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useContractsUi } from '@/hooks/useContractsUi';
import { FileText, RefreshCw } from 'lucide-react';

interface Contract {
  id: string;
  contractNumber: string;
  contractType: string;
  counterpartyName: string;
  assetName?: string;
  startDate: string;
  endDate: string;
  status: string;
}

/**
 * Догма §3: чинний → --ok; на погодженні та протермінований → --warn (увага,
 * не збій); чернетка й розірваний → приглушений. Червоного немає ніде.
 */
const STATUS_COLOR: Record<string, string> = {
  active: 'var(--ok)',
  pending: 'var(--warn)',
  expired: 'var(--warn)',
  draft: 'var(--text-dim)',
  terminated: 'var(--text-dim)',
};

export default function ContractsRedesign() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { search } = useContractsUi();

  const [items, setItems] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const loadContracts = async () => {
    setLoading(true);
    try {
      const data = await api.get<Contract[]>('/contracts');
      setItems(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setItems([]);
      } else {
        const message = getApiErrorMessage(err, 'Failed to load contracts');
        addToast(message, 'error');
      }
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => { loadContracts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => items.filter((c) =>
    c.contractNumber.toLowerCase().includes(search.toLowerCase()) ||
    c.counterpartyName.toLowerCase().includes(search.toLowerCase())
  ), [items, search]);

  const statusLabel = (v: string) => t(`contract_status_${v}`, { defaultValue: v.replace('_', ' ') });
  // Перелік типів договорів у коді не заданий, тож лишаємо м'який fallback:
  // щойно в i18n з'являться ключі contract_type_*, вони підхопляться самі.
  const typeLabel = (v: string) => t(`contract_type_${v}`, { defaultValue: v.replace('_', ' ') });

  const th: any = {
    textAlign: 'left', padding: '10px 14px', fontSize: 10.5, fontWeight: 700,
    letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)',
    background: 'var(--surface-2)', borderBottom: '1px solid var(--line)', whiteSpace: 'nowrap',
  };
  const td: any = {
    padding: '10px 14px', fontSize: 12.5, color: 'var(--text)',
    borderBottom: '1px solid var(--line)',
  };

  const statusPill = (s: string) => {
    const c = STATUS_COLOR[s] ?? 'var(--text-dim)';
    return (
      <span className="text-[10.5px] font-semibold px-2 py-0.5 whitespace-nowrap"
        style={{ borderRadius: 999, color: c, border: `1px solid ${c}`,
                 background: `color-mix(in srgb, ${c} 12%, transparent)` }}>
        {statusLabel(s)}
      </span>
    );
  };

  const firstLoad = loading && !loaded;

  return (
    <DashboardLayout breadcrumb={[t('nav_doc_contracts')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className="px-4 md:px-6 pt-5 pb-10" style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>

          {/* Заголовок у тілі не дублюємо: назву розділу показує топбар.
              Лишається лише функціональна кнопка оновлення. */}
          <div className="mb-3 flex justify-end">
            <button onClick={loadContracts} disabled={loading} title={t('contracts_loading')}
              className="inline-flex items-center justify-center cursor-pointer shrink-0"
              style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--surface-2)',
                       border: '1px solid var(--line)', color: 'var(--text-muted)',
                       opacity: loading ? 0.55 : 1 }}>
              <RefreshCw size={15} strokeWidth={2} className={loading ? 'animate-spin' : undefined} />
            </button>
          </div>

          <div className="tile overflow-hidden">
            {firstLoad ? (
              <div className="p-12 text-center">
                <RefreshCw size={22} strokeWidth={2} className="animate-spin mx-auto" style={{ color: 'var(--text-muted)' }} />
                <p className="text-[12.5px] mt-3" style={{ color: 'var(--text-muted)' }}>{t('contracts_loading')}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center">
                <FileText size={22} strokeWidth={1.8} className="mx-auto mb-3" style={{ color: 'var(--text-dim)' }} />
                <p className="text-[13px] font-medium" style={{ color: 'var(--text)' }}>{t('contracts_not_found')}</p>
                <p className="text-[11.5px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  {search ? t('contracts_filter_hint') : t('contracts_not_found_hint')}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>{t('contracts_col_number')}</th>
                      <th style={th}>{t('contracts_col_type')}</th>
                      <th style={th}>{t('contracts_col_counterparty')}</th>
                      <th style={th}>{t('contracts_col_asset')}</th>
                      <th style={th}>{t('contracts_col_period')}</th>
                      <th style={th}>{t('contracts_col_status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c) => (
                      <tr key={c.id} className="transition-colors"
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                        <td style={{ ...td, fontWeight: 600 }}>{c.contractNumber}</td>
                        <td style={{ ...td, color: 'var(--text-muted)' }}>{typeLabel(c.contractType)}</td>
                        <td style={td}>{c.counterpartyName}</td>
                        <td style={{ ...td, color: 'var(--text-muted)', fontSize: 11.5 }}>{c.assetName ?? '—'}</td>
                        <td style={{ ...td, color: 'var(--text-muted)', fontSize: 11.5, whiteSpace: 'nowrap' }}>
                          {new Date(c.startDate).toLocaleDateString('uk-UA')} — {new Date(c.endDate).toLocaleDateString('uk-UA')}
                        </td>
                        <td style={td}>{statusPill(c.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!firstLoad && filtered.length > 0 && (
            <div className="text-[11px] mt-2" style={{ color: 'var(--text-dim)' }}>
              Показано {filtered.length} з {items.length}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
